import { getLogger } from './logger';
import type {
  AudioCaptureOptions,
  ConnectionCallbacks,
  LiveKit,
  LocalAudioTrack,
  MicrophonePreference,
  NoiseFilterPreference,
  RemoteTrack,
  Room,
} from './types';

const logger = getLogger('LiveKit');

/**
 * Microphone capture constraints used for every session.
 *
 * These reduce the amount of ambient/background noise the browser sends to
 * the agent, which helps the agent's server-side voice-activity detection
 * reliably detect when the citizen has finished speaking in noisy public
 * areas. Note: these constraints are a mitigation only — the authoritative
 * end-of-speech / turn detection (endpointing, VAD) lives on the agent side.
 */
const AUDIO_CAPTURE_DEFAULTS: AudioCaptureOptions = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: false,
  // Chrome 110+/Safari only; silently ignored by browsers that don't support it.
  voiceIsolation: true,
};

/**
 * CDN URL for LiveKit's Krisp noise-cancellation filter.
 *
 * The `/+esm` endpoint is used (not the raw `dist/index.js`) because it
 * resolves the package's own bare imports — including its internal
 * `livekit-client` dependency — to ESM URLs, so it runs without a build step.
 */
const KRISP_NOISE_FILTER_URL =
  'https://cdn.jsdelivr.net/npm/@livekit/krisp-noise-filter@0.4.3/+esm';

/**
 * Options accepted by Krisp's `KrispNoiseFilter()` factory that we rely on.
 * `useBVC` selects the Background Voice Cancellation model (removes other
 * people's voices, not just ambient noise); `quality` trades CPU for accuracy.
 */
interface KrispOptions {
  useBVC?: boolean;
  quality?: 'low' | 'medium' | 'high';
}

/**
 * Minimal shape of the Krisp processor instance we depend on.
 */
interface KrispNoiseFilterProcessor {
  setEnabled(enable: boolean): Promise<boolean | undefined>;
  isEnabled(): boolean;
}

/**
 * Minimal shape of the Krisp module loaded from CDN.
 */
interface KrispNoiseFilterModule {
  KrispNoiseFilter: (options?: KrispOptions) => KrispNoiseFilterProcessor;
  isKrispNoiseFilterSupported: () => boolean;
}

/**
 * Apply LiveKit's Krisp audio processor to the local microphone.
 *
 * Two modes:
 *  - **Background Voice Cancellation (BVC)** — the default. Removes *other
 *    people's voices* as well as ambient noise, keeping only the primary
 *    speaker in front of the kiosk. This is the key mitigation for busy public
 *    areas where an omnidirectional mic (e.g. a webcam) also picks up the queue.
 *  - **Standard noise cancellation (NC)** — removes ambient/steady noise only.
 *    Used as the automatic fallback when BVC can't be loaded (deployment not
 *    entitled to the BVC model, or the device is too slow).
 *
 * Stripping this audio *before* it reaches the agent also helps the agent's
 * server-side voice-activity detection find the end of the citizen's speech
 * instead of treating background chatter as ongoing speech.
 *
 * Best-effort and non-blocking: any failure (unsupported browser, CDN/WASM load
 * error) is logged and swallowed so the session continues using the browser
 * capture constraints alone.
 */
async function applyNoiseFilter(
  track: LocalAudioTrack,
  preference?: NoiseFilterPreference
): Promise<void> {
  // Master switch: when explicitly disabled, skip Krisp entirely and rely on
  // the browser capture constraints alone. Lets a deployment A/B test the
  // filter without a code change.
  if (preference?.enabled === false) {
    logger.info(
      'Krisp noise filter disabled by configuration; using browser capture constraints only'
    );
    return;
  }

  const wantBVC = preference?.backgroundVoiceCancellation ?? true;
  const quality = preference?.quality;

  try {
    logger.debug('Loading Krisp noise filter from CDN', { url: KRISP_NOISE_FILTER_URL });
    const krisp = (await import(
      /* @vite-ignore */ KRISP_NOISE_FILTER_URL
    )) as KrispNoiseFilterModule;

    if (!krisp.isKrispNoiseFilterSupported()) {
      logger.info('Krisp noise filter not supported in this browser; skipping');
      return;
    }

    const buildOptions = (useBVC: boolean): KrispOptions => ({
      useBVC,
      ...(quality ? { quality } : {}),
    });

    // Prefer BVC (removes nearby human voices). If its model fails to load,
    // fall back to standard noise cancellation rather than losing the filter
    // entirely.
    if (wantBVC) {
      try {
        await track.setProcessor(krisp.KrispNoiseFilter(buildOptions(true)));
        logger.info('Krisp Background Voice Cancellation (BVC) applied to local microphone', {
          quality: quality ?? 'default',
        });
        return;
      } catch (bvcError) {
        logger.warn('Krisp BVC model unavailable; falling back to standard noise cancellation', {
          error: bvcError instanceof Error ? bvcError.message : bvcError,
        });
      }
    }

    await track.setProcessor(krisp.KrispNoiseFilter(buildOptions(false)));
    logger.info('Krisp standard noise cancellation applied to local microphone', {
      quality: quality ?? 'default',
    });
  } catch (error) {
    logger.warn(
      'Could not apply Krisp noise filter; continuing with browser noise suppression only',
      { error: error instanceof Error ? error.message : error }
    );
  }
}

/**
 * Resolve a deployment's {@link MicrophonePreference} to a concrete
 * `MediaDeviceInfo.deviceId` to capture from.
 *
 * Why this exists: on shared kiosk hardware the browser's *default* microphone
 * is often the wrong one — e.g. a webcam's omnidirectional mic that also picks
 * up people in the queue — while a dedicated directional / array module is the
 * device we actually want. This lets a deployment pin capture to the good mic
 * without a code change and without touching the OS default.
 *
 * Resolution order:
 *   1. `preferredLabel` (case-insensitive substring of the device label) —
 *      preferred because labels are stable across reboots/permission grants,
 *      whereas `deviceId` values rotate per origin.
 *   2. explicit `deviceId`, used when no label is given or nothing matches.
 *
 * Returns `undefined` when there is no preference or nothing could be resolved,
 * so callers fall back to the browser default. Best-effort and non-throwing.
 *
 * Note: `enumerateDevices()` only exposes device *labels* after microphone
 * permission has been granted. On a kiosk the permission is a one-time
 * persistent grant, so from the second session on the label match works; the
 * very first grant may fall back to the default until labels become available.
 */
export async function resolveMicrophoneDeviceId(
  preference?: MicrophonePreference
): Promise<string | undefined> {
  if (!preference) {
    return undefined;
  }

  const { deviceId, preferredLabel } = preference;

  if (!preferredLabel) {
    return deviceId || undefined;
  }

  try {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      logger.info('enumerateDevices unavailable; falling back to configured deviceId', {
        hasDeviceId: !!deviceId,
      });
      return deviceId || undefined;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const needle = preferredLabel.toLowerCase();
    const match = devices.find(
      (device) =>
        device.kind === 'audioinput' &&
        !!device.label &&
        device.label.toLowerCase().includes(needle)
    );

    if (match) {
      logger.info('Resolved preferred microphone by label', {
        preferredLabel,
        matchedLabel: match.label,
      });
      return match.deviceId;
    }

    logger.warn('No microphone matched preferredLabel; falling back', {
      preferredLabel,
      hasDeviceId: !!deviceId,
    });
    return deviceId || undefined;
  } catch (error) {
    logger.warn('Could not enumerate devices; falling back to configured deviceId', {
      error: error instanceof Error ? error.message : error,
      hasDeviceId: !!deviceId,
    });
    return deviceId || undefined;
  }
}

/**
 * Reference to remote audio track for visualization
 */
let remoteAudioTrack: RemoteTrack | null = null;

/**
 * Reference to local audio track for visualization
 */
let localAudioTrack: LocalAudioTrack | null = null;

/**
 * Callback to notify when remote audio track becomes available
 */
let onAudioTrackAvailable: ((track: RemoteTrack) => void) | null = null;

/**
 * Callback to notify when local audio track becomes available
 */
let onLocalAudioTrackAvailable: ((track: LocalAudioTrack) => void) | null = null;

/**
 * Register callback for when remote audio track is available
 */
export function onRemoteAudioTrack(callback: (track: RemoteTrack) => void): void {
  onAudioTrackAvailable = callback;
  // If track already exists, call immediately
  if (remoteAudioTrack) {
    callback(remoteAudioTrack);
  }
}

/**
 * Register callback for when local audio track is available
 */
export function onLocalAudioTrack(callback: (track: LocalAudioTrack) => void): void {
  onLocalAudioTrackAvailable = callback;
  // If track already exists, call immediately
  if (localAudioTrack) {
    callback(localAudioTrack);
  }
}

/**
 * Dynamically loads the LiveKit SDK from CDN
 *
 * @returns Promise that resolves to typed LiveKit object when script is loaded
 */
export async function loadLiveKit(): Promise<LiveKit> {
  logger.info('loadLiveKit called');

  return new Promise((resolve, reject) => {
    logger.debug('Checking if LiveKit SDK already loaded');
    // Check if LiveKit is already loaded
    if (window.LivekitClient) {
      logger.info('LiveKit SDK already loaded');
      resolve(window.LivekitClient);
      return;
    }

    logger.info('Loading LiveKit SDK from CDN');
    const script = document.createElement('script');
    script.src =
      'https://cdn.jsdelivr.net/npm/livekit-client@2.16.0/dist/livekit-client.umd.min.js';
    script.async = true;

    const startTime = Date.now();

    script.onload = () => {
      const loadTime = Date.now() - startTime;
      logger.debug('LiveKit script loaded', { loadTime: `${loadTime}ms` });

      if (window.LivekitClient) {
        logger.info('LiveKit SDK loaded successfully', {
          loadTime: `${loadTime}ms`,
          hasRoom: !!window.LivekitClient.Room,
        });
        resolve(window.LivekitClient);
      } else {
        logger.error('LiveKit SDK not found after loading script', { loadTime: `${loadTime}ms` });
        reject(new Error('LiveKit SDK not found after loading script'));
      }
    };

    script.onerror = (error) => {
      const loadTime = Date.now() - startTime;
      logger.error('Failed to load LiveKit SDK from CDN', {
        error,
        loadTime: `${loadTime}ms`,
        scriptSrc: script.src,
      });
      reject(new Error('Failed to load LiveKit SDK from CDN'));
    };

    logger.debug('Appending LiveKit script to document head');
    document.head.appendChild(script);
  });
}

/**
 * Connect to a LiveKit room using provided token and WebSocket URL
 *
 * @param token - JWT token for authentication
 * @param wsUrl - WebSocket URL for LiveKit server
 * @param callbacks - Event handlers for connection state changes
 * @returns Promise that resolves to Room object when connected
 */
export async function connectLiveKit(
  token: string,
  wsUrl: string,
  callbacks: ConnectionCallbacks = {},
  micPreference?: MicrophonePreference,
  noiseFilterPreference?: NoiseFilterPreference
): Promise<Room> {
  logger.info('connectLiveKit called', {
    hasToken: !!token,
    hasWsUrl: !!wsUrl,
    wsUrl: wsUrl ? '[REDACTED]' : null,
    hasCallbacks: !!callbacks,
    callbackTypes: Object.keys(callbacks),
    hasMicPreference: !!micPreference,
    hasNoiseFilterPreference: !!noiseFilterPreference,
  });

  if (!token || !wsUrl) {
    logger.error('Missing required connection parameters', {
      hasToken: !!token,
      hasWsUrl: !!wsUrl,
    });
    throw new Error('Token and wsUrl are required for LiveKit connection');
  }

  const startTime = Date.now();

  try {
    logger.debug('Loading LiveKit SDK');
    // Load LiveKit SDK
    const LiveKit = await loadLiveKit();
    const loadTime = Date.now() - startTime;

    // Resolve an optional preferred input device (e.g. a directional/array mic
    // on a kiosk) and layer it onto the noise-reducing capture defaults. Falls
    // back to the browser default device when no preference resolves.
    const resolvedDeviceId = await resolveMicrophoneDeviceId(micPreference);
    const captureOptions: AudioCaptureOptions = resolvedDeviceId
      ? { ...AUDIO_CAPTURE_DEFAULTS, deviceId: resolvedDeviceId }
      : AUDIO_CAPTURE_DEFAULTS;

    logger.debug('Creating LiveKit room instance', {
      audioCaptureDefaults: captureOptions,
      usingPreferredDevice: !!resolvedDeviceId,
    });
    // Create room instance with noise-reducing microphone capture defaults
    const room = new LiveKit.Room({ audioCaptureDefaults: captureOptions });

    logger.debug('Registering event handlers', {
      hasConnected: !!callbacks.connected,
      hasDisconnected: !!callbacks.disconnected,
      hasError: !!callbacks.error,
    });
    // Register event handlers
    if (callbacks.connected) {
      room.on('connected', callbacks.connected);
    }

    if (callbacks.disconnected) {
      room.on('disconnected', callbacks.disconnected);
    }

    if (callbacks.error) {
      room.on('error', callbacks.error);
    }

    logger.debug('Setting up track subscription handler');
    // Handle audio track subscription internally
    room.on('trackSubscribed', (track: RemoteTrack) => {
      logger.info('Track subscribed', {
        kind: track.kind,
        hasAttach: typeof track.attach === 'function',
      });

      if (track.kind === 'audio') {
        // Store reference for audio visualizer
        remoteAudioTrack = track;
        logger.debug('Remote audio track stored for visualizer');

        // Notify any waiting callback that audio track is now available
        if (onAudioTrackAvailable) {
          onAudioTrackAvailable(track);
        }

        // Auto-attach audio track for playback
        try {
          logger.debug('Auto-attaching audio track');
          track.attach();
          logger.info('Audio track attached successfully');
        } catch (error) {
          logger.warn('Failed to attach audio track', { error });
        }
      }
    });

    logger.info('Connecting to LiveKit room', {
      loadTime: `${loadTime}ms`,
      wsUrl: '[REDACTED]',
    });
    // Connect to the room
    await room.connect(wsUrl, token);
    const connectTime = Date.now() - startTime;

    logger.info('Connected to LiveKit room successfully', {
      connectTime: `${connectTime}ms`,
      roomState: 'connected',
    });

    logger.debug('Enabling microphone');
    // Enable microphone to send audio to agent
    try {
      // setMicrophoneEnabled returns a Promise<LocalTrackPublication>.
      // Pass the capture constraints explicitly as well so they apply even if
      // the SDK ignores the room-level audioCaptureDefaults for this call.
      const micPublication = await room.localParticipant.setMicrophoneEnabled(true, captureOptions);

      // Capture local audio track for visualization from the publication
      if (micPublication && micPublication.track) {
        localAudioTrack = micPublication.track;

        // Attach Krisp noise cancellation (best-effort, non-blocking) so we
        // don't delay the connected/streaming flow while the WASM model loads.
        void applyNoiseFilter(localAudioTrack!, noiseFilterPreference);

        logger.info('Local audio track captured for visualization', {
          trackName: micPublication.trackName,
          trackSid: micPublication.trackSid,
          trackType: localAudioTrack?.constructor?.name,
          hasMediaStreamTrack: localAudioTrack && 'mediaStreamTrack' in localAudioTrack,
          kind: micPublication.kind,
        });

        // Notify any waiting callback that local audio track is now available
        if (onLocalAudioTrackAvailable) {
          logger.debug('Calling onLocalAudioTrackAvailable callback');
          onLocalAudioTrackAvailable(localAudioTrack!);
        }
      } else {
        logger.warn('Microphone publication returned without track', {
          hasPublication: !!micPublication,
          hasTrack: !!micPublication?.track,
        });
      }

      logger.info('Microphone enabled successfully');
    } catch (error) {
      // Microphone permission denied or error, but don't fail the connection
      logger.warn('Could not enable microphone', {
        error: error instanceof Error ? error.message : error,
        name: error instanceof Error ? error.name : 'Unknown',
      });
      logger.info('Continuing without microphone (user may have denied permission)');
    }

    logger.info('connectLiveKit completed successfully', {
      totalTime: `${Date.now() - startTime}ms`,
      hasMicrophone: true, // If no error was thrown, assume microphone is available
    });

    return room;
  } catch (error) {
    const totalTime = Date.now() - startTime;
    logger.error('Failed to connect to LiveKit', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      totalTime: `${totalTime}ms`,
      name: error instanceof Error ? error.name : 'Unknown',
    });
    if (error instanceof Error) {
      throw new Error(`Failed to connect to LiveKit: ${error.message}`);
    }
    throw new Error('Unknown error occurred while connecting to LiveKit');
  }
}

/**
 * Get the current remote audio track for visualization
 */
export function getRemoteAudioTrack(): RemoteTrack | null {
  return remoteAudioTrack;
}

/**
 * Get the current local audio track for visualization
 */
export function getLocalAudioTrack(): LocalAudioTrack | null {
  return localAudioTrack;
}

/**
 * Disconnect from a LiveKit room
 *
 * @param room - Room instance to disconnect
 */
export function disconnect(room: Room): void {
  logger.info('disconnect called');

  if (!room) {
    logger.warn('Attempted to disconnect from null/undefined room');
    return;
  }

  try {
    logger.debug('Disconnecting from LiveKit room');
    room.disconnect();

    // Clear audio track reference and callback
    remoteAudioTrack = null;
    localAudioTrack = null;
    onAudioTrackAvailable = null;
    onLocalAudioTrackAvailable = null;

    logger.info('Disconnected from LiveKit room successfully');
  } catch (error) {
    logger.error('Error disconnecting from LiveKit room', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    });
  }
}

// Export types for external use
export type { LiveKit as LiveKitType };
